import React, { useEffect, useRef, useState } from "react";
import { AnswerResult } from "../Quiz/answer.ts";
import { cardFaces } from "../cardFaces.ts";
import type { PickIndex } from "./chartGame.ts";
import {
    answer,
    choose,
    chooseTurn,
    Clip,
    FACTORS,
    gameProblem,
    isLegal,
    move,
    newGame,
    Player,
    ProductGameState,
    ProductVariant,
    SIDE,
} from "./productGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

export type ProductMode = "computer" | "two";

export interface ProductGameResult {
    variant: ProductVariant;
    mode: ProductMode;
    winner: Player | "draw";
    /** Human answers only - the computer never types. */
    correct: number;
    wrong: number;
}

interface ProductGameProps {
    /** Product Game (x) or Sum Game (+); fixed for the life of the component. */
    variant?: ProductVariant;
    mode: ProductMode;
    pick: PickIndex;
    onAnswer(front: string, result: AnswerResult): void;
    onFinish(result: ProductGameResult): void;
}

const CLIP_NAMES = ["A", "B"];
const MARKS = ["●", "■"];
/** Long enough to see what the computer did before it is your turn again. */
export const COMPUTER_DELAY_MS = 700;

/**
 * The Product Game (ADR 0055) and the Sum Game (ADR 0098 §3): a board of
 * products or sums, two number clips, four in a row wins. A human move asks for
 * the answer before it claims the square (§2); when the answer is on more than
 * one free square the player taps which. The computer moves after a short
 * pause and never types.
 */
export function ProductGame({ variant = "product", mode, pick, onAnswer, onFinish }: ProductGameProps) {
    const t = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [game, setGame] = useState<ProductGameState>(() => newGame(variant, pick));
    const [typed, setTyped] = useState("");
    const [verdict, setVerdict] = useState<{ text: string; result: AnswerResult } | null>(null);
    const [computerMove, setComputerMove] = useState<{ name: string; n: number; product: number } | null>(null);
    const counts = useRef({ correct: 0, wrong: 0 });
    const reported = useRef(false);

    const sum = variant === "sum";
    const computerTurn = mode === "computer" && game.turn === 1;
    const humanCanMove = !computerTurn && game.phase === "playing";
    const answering = !computerTurn && game.phase === "answering";
    const choosing = !computerTurn && game.phase === "choosing";
    const rows = Array.from({ length: SIDE }, (_, r) => game.board.slice(r * SIDE, r * SIDE + SIDE));

    useEffect(() => {
        if (!computerTurn || game.phase !== "playing") {
            return;
        }
        const timer = setTimeout(() => {
            const turn = chooseTurn(game, pick);
            if (!turn) {
                return;
            }
            const m = turn.move;
            let next = move(game, m);
            const product = next.pending?.product;
            if (next.phase === "answering" && product !== undefined) {
                next = answer(next, String(product)).state;
                // The computer picks its own square when the sum is on several.
                if (next.phase === "choosing" && turn.square !== null) {
                    next = choose(next, turn.square);
                }
                setComputerMove({ name: CLIP_NAMES[m.clip], n: m.value, product });
            }
            setGame(next);
        }, COMPUTER_DELAY_MS);
        return () => clearTimeout(timer);
    }, [computerTurn, game, pick]);

    useEffect(() => {
        if (answering) {
            inputRef.current?.focus();
        }
    }, [answering]);

    useEffect(() => {
        if (game.phase === "over" && game.winner !== null && !reported.current) {
            reported.current = true;
            onFinish({ variant, mode, winner: game.winner, ...counts.current });
        }
    }, [game, variant, mode, onFinish]);

    function play(clip: Clip, value: number) {
        if (!humanCanMove || !isLegal(game, { clip, value })) {
            return;
        }
        setTyped("");
        setGame(move(game, { clip, value }));
    }

    function claim(index: number) {
        if (choosing) {
            setGame(choose(game, index));
        }
    }

    function submitAnswer() {
        if (!answering || !game.pending) {
            return;
        }
        const faces = cardFaces(gameProblem(variant, game.pending.a, game.pending.b));
        const { state, result } = answer(game, typed);
        counts.current[result === "correct" ? "correct" : "wrong"] += 1;
        onAnswer(faces.front, result);
        setVerdict({ text: `${faces.front} = ${faces.back}`, result });
        setTyped("");
        setGame(state);
    }

    function playerName(player: Player): string {
        return format(t("flashCards.productPlayerTurnTemplate"), { n: player + 1 });
    }

    function status(): string {
        if (game.phase === "over") {
            if (game.winner === "draw") {
                return t("flashCards.productDraw");
            }
            if (mode === "computer") {
                return game.winner === 0 ? t("flashCards.productYouWin") : t("flashCards.productComputerWins");
            }
            return format(t("flashCards.productPlayerWinsTemplate"), { n: (game.winner as Player) + 1 });
        }
        if (computerTurn) {
            return t("flashCards.productComputerThinking");
        }
        return mode === "computer" ? t("flashCards.productYourTurn") : playerName(game.turn);
    }

    function instruction(): string {
        if (game.phase === "over" || computerTurn) {
            return "";
        }
        if (answering && game.pending) {
            const front = cardFaces(gameProblem(variant, game.pending.a, game.pending.b)).front;
            return format(
                sum ? t("flashCards.productSumTypeTemplate") : t("flashCards.productTypeProductTemplate"),
                { front }
            );
        }
        if (choosing) {
            return t("flashCards.productChooseSquare");
        }
        if (game.clips[0] === null) {
            return sum ? t("flashCards.productSumPlaceFirstClip") : t("flashCards.productPlaceFirstClip");
        }
        if (game.clips[1] === null) {
            return sum ? t("flashCards.productSumPlaceSecondClip") : t("flashCards.productPlaceSecondClip");
        }
        return sum ? t("flashCards.productSumMoveOneClip") : t("flashCards.productMoveOneClip");
    }

    function cellLabel(product: number, owner: Player | null): string {
        if (owner === null) {
            return format(t("flashCards.productCellFreeTemplate"), { product });
        }
        if (mode === "computer") {
            return format(
                owner === 0 ? t("flashCards.productCellYoursTemplate") : t("flashCards.productCellComputerTemplate"),
                { product }
            );
        }
        return format(t("flashCards.productCellPlayerTemplate"), { product, n: owner + 1 });
    }

    const pendingFront = game.pending ? cardFaces(gameProblem(variant, game.pending.a, game.pending.b)).front : "";
    const winCells = new Set(game.winLine ?? []);
    const offered = new Set(choosing ? game.candidates ?? [] : []);

    return (
        <div className="productGame">
            <p className={`productStatus productStatus--p${game.turn}`} aria-live="polite">
                {status()}
            </p>
            <p className="productInstruction">{instruction()}</p>

            <table className="productBoard">
                <caption className="chartCaption">
                    {sum ? t("flashCards.productSumBoardCaption") : t("flashCards.productBoardCaption")}
                </caption>
                <tbody>
                    {rows.map((row, r) => (
                        <tr key={r}>
                            {row.map((product, c) => {
                                const index = r * SIDE + c;
                                const owner = game.owners[index];
                                const classes = [
                                    "productCell",
                                    owner !== null ? `productCell--p${owner}` : "",
                                    winCells.has(index) ? "productCell--win" : "",
                                    offered.has(index) ? "productCell--offered" : "",
                                    game.lastClaim === index ? "productCell--last" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");
                                return (
                                    <td key={index} className={classes} aria-label={cellLabel(product, owner)}>
                                        {owner !== null && (
                                            <span className="productMark" aria-hidden="true">
                                                {MARKS[owner]}
                                            </span>
                                        )}
                                        {offered.has(index) ? (
                                            <button
                                                type="button"
                                                className="productCellButton"
                                                aria-label={format(t("flashCards.productCellChooseTemplate"), { product })}
                                                onClick={() => claim(index)}
                                            >
                                                {product}
                                            </button>
                                        ) : (
                                            product
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="productClips">
                {([0, 1] as Clip[]).map((clip) => (
                    <div key={clip} className="productClipRow">
                        <span className="productClipLabel">
                            {format(t("flashCards.productClipLabelTemplate"), { name: CLIP_NAMES[clip] })}
                        </span>
                        <div className="productFactors">
                            {FACTORS.map((value) => {
                                const here = game.clips[clip] === value;
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        className={`productFactor${here ? " productFactor--here" : ""}`}
                                        aria-pressed={here}
                                        aria-label={format(t("flashCards.productMoveClipAriaTemplate"), {
                                            name: CLIP_NAMES[clip],
                                            n: value,
                                        })}
                                        disabled={!humanCanMove || !isLegal(game, { clip, value })}
                                        onClick={() => play(clip, value)}
                                    >
                                        {value}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <form
                className="quizCard chartAsk"
                onSubmit={(e) => {
                    e.preventDefault();
                    submitAnswer();
                }}
            >
                <div className="chartAnswerRow">
                    <span className="productPending" aria-hidden="true">
                        {answering ? `${pendingFront} =` : ""}
                    </span>
                    <input
                        ref={inputRef}
                        className={`quizInput chartInput${verdict ? ` quizInput--${verdict.result}` : ""}`}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value.replace(/[^\d]/g, ""))}
                        onKeyDown={(e) => {
                            // As on the quiz: implicit submission on Enter does not always fire.
                            if (e.key === "Enter") {
                                e.preventDefault();
                                submitAnswer();
                            }
                        }}
                        disabled={!answering}
                        aria-label={format(t("flashCards.answerForAriaTemplate"), { front: pendingFront })}
                    />
                    <button type="submit" className="quizSubmit" disabled={!answering}>
                        {t("flashCards.enterButton")}
                    </button>
                </div>
            </form>

            <p className={`quizVerdict${verdict ? ` quizVerdict--${verdict.result}` : ""}`} aria-live="polite">
                {verdict && (
                    <>
                        {verdict.result === "correct" ? t("flashCards.correct") : t("flashCards.notQuite")}
                        {" · "}
                        {verdict.text}
                        {verdict.result !== "correct" && `. ${t("flashCards.productTurnPasses")}`}
                    </>
                )}
            </p>

            {mode === "computer" && (
                <p className="productComputerNote">
                    {computerMove &&
                        format(
                            sum
                                ? t("flashCards.productSumComputerMovedTemplate")
                                : t("flashCards.productComputerMovedTemplate"),
                            computerMove
                        )}
                </p>
            )}
        </div>
    );
}
